/**
 * Front-end error reporter — sends crashes + unhandled rejections + failed
 * API calls to /api/frontend-error, with correlation IDs that match the
 * backend's request_id so the Day Dashboard can drill from a FE crash to
 * the exact server request that preceded it.
 *
 * Owner-spec 2026-05-26 (load-test prep):
 *   - "shgia lo tzfuya" (unexpected error) → we want to know what caused it
 *   - dedupe key includes the FE build hash so a new deploy doesn't merge
 *     with old occurrences
 *   - last 5 API calls are captured by wrapping window.fetch (lightweight)
 *
 * This module is fire-and-forget. It NEVER throws. Importing it has the
 * side effect of:
 *   1. Wrapping window.fetch to capture request_id + duration + status
 *   2. Installing window.onerror + window.onunhandledrejection handlers
 *   3. Generating a stable session id for this browser tab
 *
 * Public API:
 *   reportError(info)       — manual report from inside ErrorBoundary, etc.
 *   getBuildHash()          — bundle hash for dedupe / release marker
 *   getSessionId()          — UUID stable for this tab session
 */

import config from '../config/config';
import { getSessionToken } from './session';

let _wrapped = false;
let _installed = false;

// ---- session token on our own API calls -----------------------------------
// 2026-10-15 (Setup auth, stage 1). About 250 fetch calls across the Setup
// screens send no Authorization header, so the server sees them as anonymous —
// which is why those endpoints could not be admin-gated without breaking the
// screens. Rather than editing every call site, the wrapper below adds
// `Authorization: Bearer <session_token>` to requests for OUR backend's /api/
// when the caller did not set one itself. A caller's own header always wins.
// Other hosts (Infobip, Google, the telephony server on :3010) are untouched.
function _isOwnApi(url) {
  try {
    const u = new URL(url, window.location.href);
    if (!u.pathname.startsWith('/api/')) return false;
    if (u.origin === window.location.origin) return true;
    const base = config.api.baseUrl;
    return !!base && u.origin === new URL(base, window.location.href).origin;
  } catch {
    return false;
  }
}

// ---- session id ----------------------------------------------------------
const SESSION_KEY = '_afikim_fe_session_id';
let _sessionId = null;
function _initSessionId() {
  try {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) {
      s = (crypto && crypto.randomUUID) ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, s);
    }
    _sessionId = s;
  } catch {
    _sessionId = `s-fallback-${Date.now()}`;
  }
}
export function getSessionId() {
  if (!_sessionId) _initSessionId();
  return _sessionId;
}

// ---- build hash ----------------------------------------------------------
let _buildHash = '';
function _extractBuildHash() {
  // CRA emits main.{hash}.js — find ours in the loaded script tags.
  try {
    const scripts = document.querySelectorAll('script[src]');
    for (const s of scripts) {
      const m = s.src.match(/main\.([0-9a-f]+)\.js/);
      if (m) return m[1];
    }
  } catch { /* noop */ }
  return '';
}
export function getBuildHash() {
  if (!_buildHash) _buildHash = _extractBuildHash();
  return _buildHash || 'unknown';
}

// ---- last API calls (rolling buffer, max 5) ------------------------------
const MAX_API_CALLS = 5;
const _lastApiCalls = [];

function _recordApiCall(entry) {
  _lastApiCalls.push(entry);
  while (_lastApiCalls.length > MAX_API_CALLS) _lastApiCalls.shift();
}

// ---- fetch wrapper -------------------------------------------------------
function _installFetchWrap() {
  if (_wrapped) return;
  if (typeof window === 'undefined' || !window.fetch) return;
  const origFetch = window.fetch.bind(window);

  window.fetch = async function (input, init = {}) {
    let url = '';
    let method = 'GET';
    try {
      if (typeof input === 'string') {
        url = input;
        method = (init && init.method) || 'GET';
      } else if (input && input.url) {
        url = input.url;
        method = input.method || 'GET';
      }
    } catch { /* ignore */ }

    // Generate a fresh X-Request-Id for backend correlation if caller didn't
    // already set one. Keep short (12 hex) — matches backend uuid4().hex[:12].
    let reqId = '';
    try {
      const hdrs = new Headers((init && init.headers) || (input && input.headers) || {});
      reqId = hdrs.get('X-Request-Id') || hdrs.get('x-request-id') || '';
      if (!reqId) {
        reqId = (crypto && crypto.randomUUID)
          ? crypto.randomUUID().replace(/-/g, '').slice(0, 12)
          : Math.random().toString(16).slice(2, 14);
        hdrs.set('X-Request-Id', reqId);
        hdrs.set('X-Session-Id', getSessionId());
        init = { ...(init || {}), headers: hdrs };
      }
    } catch { /* ignore */ }

    try {
      if (_isOwnApi(url)) {
        const hdrs = new Headers((init && init.headers) || (input && input.headers) || {});
        if (!hdrs.has('Authorization')) {
          const token = getSessionToken();
          if (token) {
            hdrs.set('Authorization', `Bearer ${token}`);
            init = { ...(init || {}), headers: hdrs };
          }
        }
      }
    } catch { /* ignore */ }

    const startedAt = (performance && performance.now) ? performance.now() : Date.now();
    let status = 0;
    let networkError = null;
    try {
      const resp = await origFetch(input, init);
      status = resp.status;
      return resp;
    } catch (err) {
      networkError = (err && err.message) || String(err);
      throw err;
    } finally {
      const dur = ((performance && performance.now) ? performance.now() : Date.now()) - startedAt;
      _recordApiCall({
        request_id: reqId,
        method,
        // Only path + first 80 chars of query — never the full URL incl. host.
        path: _stripUrl(url).slice(0, 200),
        status,
        duration_ms: Math.round(dur),
        network_error: networkError,
        ts: new Date().toISOString(),
      });
      // Out-of-band: a 5xx or network failure is itself worth a FE event.
      if (networkError || status >= 500) {
        _send({
          category: networkError ? 'network_error' : 'api_error',
          message: networkError
            ? `network error: ${networkError}`
            : `HTTP ${status} from ${_stripUrl(url)}`,
          stack: '',
          componentStack: '',
          route: _safeRoute(),
        });
      }
    }
  };

  _wrapped = true;
}

function _stripUrl(u) {
  try {
    const parsed = new URL(u, window.location.origin);
    return parsed.pathname + (parsed.search || '');
  } catch {
    return String(u || '');
  }
}

function _safeRoute() {
  try {
    return window.location.pathname + window.location.search;
  } catch {
    return '';
  }
}

// ---- dedupe (per-tab; backend has its own grouping in the dashboard) -----
const _seen = new Map(); // dedupeKey -> last-reported timestamp
const DEDUPE_WINDOW_MS = 30 * 1000; // 30s

function _firstStackFrame(stack) {
  if (!stack) return '';
  const line = String(stack).split('\n').find(l => l && !l.includes('at <anonymous>')) || '';
  // strip ?query / leading 'at ' / cwd-ish noise
  return line.trim().replace(/\?.*$/, '').slice(0, 120);
}

function _dedupeKey(info) {
  return [
    getBuildHash(),
    info.route || '',
    (info.message || '').slice(0, 150),
    _firstStackFrame(info.stack),
  ].join('|');
}

function _shouldSend(key) {
  const now = Date.now();
  const last = _seen.get(key);
  if (last && (now - last) < DEDUPE_WINDOW_MS) return false;
  _seen.set(key, now);
  // Bound the map; not critical, but be tidy
  if (_seen.size > 200) {
    const cutoff = now - DEDUPE_WINDOW_MS;
    for (const [k, t] of _seen) {
      if (t < cutoff) _seen.delete(k);
    }
  }
  return true;
}

// ---- send to /api/frontend-error ----------------------------------------
function _send(info) {
  try {
    const dedupeKey = _dedupeKey(info);
    if (!_shouldSend(dedupeKey)) return;

    let userId = '';
    try {
      // Best-effort: pull username from local/session storage if app stored it
      userId = localStorage.getItem('username')
            || sessionStorage.getItem('username')
            || '';
    } catch { /* ignore */ }

    const payload = {
      category: info.category || 'unhandled',
      message: String(info.message || '').slice(0, 500),
      stack: String(info.stack || '').slice(0, 4000),
      componentStack: String(info.componentStack || '').slice(0, 2000),
      route: info.route || _safeRoute(),
      userId,
      sessionId: getSessionId(),
      buildHash: getBuildHash(),
      dedupeKey,
      lastApiCalls: _lastApiCalls.slice(),
      viewport: typeof window !== 'undefined'
        ? `${window.innerWidth}x${window.innerHeight}`
        : '',
      userAgent: (navigator && navigator.userAgent) || '',
      perf: _perfSnapshot(),
    };

    // Use sendBeacon when available (works during unload too)
    try {
      if (navigator && navigator.sendBeacon) {
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        const ok = navigator.sendBeacon('/api/frontend-error', blob);
        if (ok) return;
      }
    } catch { /* fall through to fetch */ }

    // Note: using the WRAPPED fetch means this call shows up in
    // _lastApiCalls too — that's fine; it can't recurse because /api/
    // frontend-error returns 200 even on internal failure.
    fetch('/api/frontend-error', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => { /* never throw */ });
  } catch {
    /* never throw */
  }
}

function _perfSnapshot() {
  try {
    const p = {};
    if (performance && performance.memory) {
      p.js_heap_used_mb = Math.round(performance.memory.usedJSHeapSize / (1024 * 1024));
      p.js_heap_total_mb = Math.round(performance.memory.totalJSHeapSize / (1024 * 1024));
    }
    if (performance && performance.now) {
      p.t_ms_since_navigation = Math.round(performance.now());
    }
    return p;
  } catch {
    return {};
  }
}

// ---- public API ---------------------------------------------------------
export function reportError(info) {
  _send({ category: 'unhandled', ...info });
}

// Idempotent installer ----------------------------------------------------
export function installFrontendErrorReporter() {
  if (_installed) return;
  _installed = true;
  _initSessionId();
  _installFetchWrap();
  try {
    window.addEventListener('error', (ev) => {
      _send({
        category: 'window_error',
        message: ev.message || 'window.onerror',
        stack: (ev.error && ev.error.stack) || '',
        route: _safeRoute(),
      });
    });
    window.addEventListener('unhandledrejection', (ev) => {
      const reason = ev.reason;
      _send({
        category: 'promise_rejection',
        message: (reason && (reason.message || String(reason))) || 'unhandled rejection',
        stack: (reason && reason.stack) || '',
        route: _safeRoute(),
      });
    });
  } catch { /* never throw */ }
}
